import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction, Paper, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ReactComponent as ActiveCalendarIcon } from 'src/assets/icons/footer/active-calendar-icon.svg';
import { ReactComponent as ActiveHomeIcon } from 'src/assets/icons/footer/active-home-icon.svg';
import { ReactComponent as ActiveMyPageIcon } from 'src/assets/icons/footer/active-myPage-icon.svg';
import { ReactComponent as ActiveNicojinIcon } from 'src/assets/icons/footer/active-nicojin-icon.svg';
import { ReactComponent as InactiveCalendarIcon } from 'src/assets/icons/footer/inactive-calendar-icon.svg';
import { ReactComponent as InactiveHomeIcon } from 'src/assets/icons/footer/inactive-home-icon.svg';
import { ReactComponent as InactiveMyPageIcon } from 'src/assets/icons/footer/inactive-myPage-icon.svg';
import { ReactComponent as InactiveNicojinIcon } from 'src/assets/icons/footer/inactive-nicojin-icon.svg';
import { FOOTER_HEIGHT, SERVICE_CONTROL_PAGE_PATH } from 'src/assets/static/constants';
import { useClickTracking } from 'src/hooks/useClickTracking';
import { PATH_PAGE } from 'src/routes/paths';

// -----------------------------------------------------------------

interface BottomNavItem {
  id: string;
  label: string;
  pathname: string;
  activeIcon: ReactNode;
  inactiveIcon: ReactNode;
}

function Footer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { withTracking } = useClickTracking();
  const { t } = useTranslation();

  const [value, setValue] = useState<number>(-1);

  const BottomNavItems = useMemo(
    () => [
      {
        id: SERVICE_CONTROL_PAGE_PATH.home,
        label: t('home.title'),
        pathname: PATH_PAGE.home,
        activeIcon: <ActiveHomeIcon />,
        inactiveIcon: <InactiveHomeIcon />,
      },
      {
        id: SERVICE_CONTROL_PAGE_PATH.drJin,
        label: t('drJin.title'),
        pathname: PATH_PAGE.drJin.root,
        activeIcon: <ActiveNicojinIcon />,
        inactiveIcon: <InactiveNicojinIcon />,
      },
      {
        id: SERVICE_CONTROL_PAGE_PATH.calendar,
        label: t('calendar.title'),
        pathname: PATH_PAGE.calendar.root,
        activeIcon: <ActiveCalendarIcon />,
        inactiveIcon: <InactiveCalendarIcon />,
      },
      {
        id: SERVICE_CONTROL_PAGE_PATH.myPage,
        label: t('myPage.title'),
        pathname: PATH_PAGE.myPage.root,
        activeIcon: <ActiveMyPageIcon />,
        inactiveIcon: <InactiveMyPageIcon />,
      },
    ],
    [t],
  );

  useEffect(() => {
    const index = BottomNavItems.findIndex((item: BottomNavItem) => item.pathname === pathname);
    setValue(index);
  }, [pathname, BottomNavItems]);

  return (
    <Container elevation={3}>
      <BottomNavigation
        showLabels
        value={value}
        onChange={(event, newValue) => {
          setValue(newValue);
        }}
        sx={{
          justifyContent: 'space-around',
          height: FOOTER_HEIGHT,
        }}
      >
        {BottomNavItems.map((item: BottomNavItem, index) => {
          return (
            <BottomNavigationAction
              key={index}
              label={item.label}
              value={index}
              onClick={withTracking(item.id, () => {
                if (pathname !== item.pathname) {
                  navigate(item.pathname);
                }
              })}
              icon={value === index ? item.activeIcon : item.inactiveIcon}
            />
          );
        })}
      </BottomNavigation>
    </Container>
  );
}
export default observer(Footer);

const Container = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  boxShadow: '0px -5px 10px 0px #0000000D',
  '& .MuiBottomNavigationAction-label': {
    cursor: 'pointer',
    fontSize: '12px',
    lineHeight: '18px',
    fontWeight: 700,
    color: theme.palette.grey[400],
    marginTop: '4px',
    '&.Mui-selected': {
      fontSize: '12px',
      lineHeight: '18px',
      fontWeight: 700,
      color: theme.palette.primary.main,
    },
  },
}));
